import {Tracker, TrackerDestination} from './tracker';
import {Injectable} from '@angular/core';
import { SegmentService } from 'ngx-segment-analytics';
import { DashboardUtils } from 'src/app/utils/dashboard/dashboard-utils';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';
import { audienseAccountId, CHURNZERO_CONTACT_PROPERTY, stampAccountAndContact } from './churnzero-identity';

@Injectable({
  providedIn: 'root'
})
export class TrackerSegment implements Tracker {
  constructor(private sessionProvider: SessionProviderService, private segment: SegmentService) {}

  private static productIdentifier = 'soprism';
  private static eventPrefix = 'soprism_';

  identify(userId: string, properties: { [propertyName: string]: any }, groupInfo?: {id: string, traits?: any }): void {
    if (this.isImpersonating()) { return; }

    const enableVitaly = !!groupInfo?.id;

    const options = {
      integrations: {
        [TrackerDestination.VITALY]: enableVitaly,
      }
    };

    properties.impersonation = this.sessionProvider.activeSession.impersonation;
    
    if (groupInfo?.id) {
      properties.account_id = [groupInfo.id];
    }
    if (userId) { properties[CHURNZERO_CONTACT_PROPERTY] = userId; }
    this.segment.identify(userId, properties, options);
    
    if (groupInfo?.id) {
      this.segment.group(groupInfo.id, groupInfo?.traits ?? {});
    }
  }

  track(event: string, properties: { [propertyName: string]: any }): void {
    if (this.isImpersonating()) { return; }

    properties.product = TrackerSegment.productIdentifier;

    const options = {
      integrations: {
        [TrackerDestination.VITALY]: false,
      }
    };

    if (this.sessionProvider.activeSession) {
      const company = this.sessionProvider.activeSession.user.company;
      if (!properties.company) { properties.company = company.name; }
      if (!properties.plan) { properties.plan = company.subscription.plan.name; }
      if (!properties.user) { properties.user = this.sessionProvider.activeSession.user.email; }
      stampAccountAndContact(
        properties,
        audienseAccountId(properties.account_id) ?? audienseAccountId(company.audiense_id),
        this.sessionProvider.activeSession.user.oauth_id
      );

      properties.impersonation = this.sessionProvider.activeSession.impersonation;

      options.integrations![TrackerDestination.VITALY] = !!this.sessionProvider.activeSession.user?.company?.audiense_id;
    }

    // Send the event to Segment
    this.segment.track(`${TrackerSegment.eventPrefix}${event}`, properties, options);
  }

  page(url: string): void {
    if (this.isImpersonating()) { return; }

    const options = {
      integrations: {
        [TrackerDestination.VITALY]: false,
      }
    };

    const properties: any = {
      product: TrackerSegment.productIdentifier,
      normalizedPath: DashboardUtils.normalizeUrl(url) // We know it's a path because the value coming from value.urlAfterRedirects (defined by Angular) is a path
    };

    if (this.sessionProvider.activeSession){
      options.integrations![TrackerDestination.VITALY] = !!this.sessionProvider.activeSession.user?.company?.audiense_id;

      properties.impersonation = this.sessionProvider.activeSession.impersonation;
      const user = this.sessionProvider.activeSession.user;
      stampAccountAndContact(properties, audienseAccountId(user?.company?.audiense_id), user?.oauth_id);
    }

    this.segment.page(undefined, properties, options);
  }

  // Staff acting as a customer, through Soprism's impersonation or the platform's (Frontegg), is not the customer's usage.
  private isImpersonating(): boolean {
    const session = this.sessionProvider.activeSession;
    return !!(session?.impersonation || session?.fronteggImpersonation);
  }
}
